import { Fragment } from "react/jsx-runtime";
import { useNavigate } from "react-router-dom";
import { tv } from "tailwind-variants";
import { t } from "ttag";

import { TESTOSTERONE_STEP_ICONS } from "@/features/testosterone-therapy/utils";
import { LogoLoader } from "@/shared/components";
import type {
  TestosteroneStep,
  TestosteroneStepAction,
} from "@/shared/domains";
import {
  TESTOSTERONE_STEP_ACTION_TYPES,
  TESTOSTERONE_STEP_STATUS,
  TESTOSTERONE_STEP_TYPES,
} from "@/shared/domains";
import { useBoolean, useDownloadPdf } from "@/shared/hooks";
import {
  IS_WEB_VIEW,
  sendMessageToReactNativeWebView,
  WEB_VIEW_ACTIONS,
} from "@/shared/lib";
import {
  Badge,
  Button,
  Card,
  CheckCircleIcon,
  CircleIcon,
  SquareBottomUpIcon,
} from "@/shared/ui";
import { ExternalActionConfirmModal } from "./";

const styles = tv({
  slots: {
    root: "grid grid-cols-[1fr_auto] gap-x-1 gap-y-0 rounded-lg border border-primary-100 bg-transparent p-3 sm:grid-cols-[1fr_auto_auto] [&_[data-slot=card-title]]:text-neutral-950",
  },
  variants: {
    status: {
      [TESTOSTERONE_STEP_STATUS.IN_PROGRESS]: {
        root: "border-primary-300 bg-primary-50",
      },
      [TESTOSTERONE_STEP_STATUS.INCOMPLETE]: "",
      [TESTOSTERONE_STEP_STATUS.COMPLETED]: "",
    },
  },
});

const COLORS = {
  [TESTOSTERONE_STEP_STATUS.IN_PROGRESS]: "warning",
  [TESTOSTERONE_STEP_STATUS.INCOMPLETE]: "gray",
  [TESTOSTERONE_STEP_STATUS.COMPLETED]: "success",
} as const;

export const TherapyStep = ({ type, status, actions }: TestosteroneStep) => {
  const navigate = useNavigate();

  const {
    value: showActionModal,
    setTrue: openActionModal,
    setFalse: closeActionModal,
  } = useBoolean();

  const { root } = styles({ status });

  const downloadPdf = useDownloadPdf();

  const handleAction = (action: TestosteroneStepAction) => {
    if (action.isExternal) {
      return openActionModal();
    }

    if (action.type === TESTOSTERONE_STEP_ACTION_TYPES.GET_REQUISITION_FORM) {
      if (IS_WEB_VIEW) {
        return sendMessageToReactNativeWebView({
          type: WEB_VIEW_ACTIONS.DOWNLOAD_FILE,
          payload: {
            url: action.url,
            filename: "lab-requisition-form.pdf",
          },
        });
      }

      return downloadPdf.mutate({
        url: action.url,
        filename: "lab-requisition-form.pdf",
      });
    }

    return navigate(action.url);
  };

  const TESTOSTERONE_STEP_TITLES = {
    [TESTOSTERONE_STEP_TYPES.FORM]: t`Form`,
    [TESTOSTERONE_STEP_TYPES.INTAKE_FORM]: t`Intake form`,
    [TESTOSTERONE_STEP_TYPES.BIOMARKERS_TEST]: t`Biomarkers test`,
    [TESTOSTERONE_STEP_TYPES.PROVIDER_CONSULTATION]: t`Provider consultation`,
  } as const;

  const TESTOSTERONE_STEP_SUBTITLES = {
    [TESTOSTERONE_STEP_TYPES.FORM]: "(5 min)",
    [TESTOSTERONE_STEP_TYPES.INTAKE_FORM]: "(5 min)",
    [TESTOSTERONE_STEP_TYPES.BIOMARKERS_TEST]: "",
    [TESTOSTERONE_STEP_TYPES.PROVIDER_CONSULTATION]: "(30 min)",
  } as const;

  const ACTION_LABEL = {
    [TESTOSTERONE_STEP_ACTION_TYPES.COMPLETE_FORM]: t`Complete`,
    [TESTOSTERONE_STEP_ACTION_TYPES.COMPLETE_INTAKE_FORM]: t`Complete`,
    [TESTOSTERONE_STEP_ACTION_TYPES.GET_REQUISITION_FORM]: t`Get requisition form`,
    [TESTOSTERONE_STEP_ACTION_TYPES.SCHEDULE_LAB]: t`Schedule now`,
    [TESTOSTERONE_STEP_ACTION_TYPES.SCHEDULE_CONSULTATION]: t`Schedule now`,
  } as const;

  return (
    <Card.Root className={root()}>
      <LogoLoader show={downloadPdf.isPending} fullscreen />

      <div className="flex grow items-baseline gap-1 text-nowrap">
        <Card.Title
          level={3}
          weight={
            status !== TESTOSTERONE_STEP_STATUS.IN_PROGRESS ? "weak" : undefined
          }
        >
          {TESTOSTERONE_STEP_TITLES[type]}
        </Card.Title>

        <Card.Description className="text-xs font-normal">
          {TESTOSTERONE_STEP_SUBTITLES[type]}
        </Card.Description>

        {status === TESTOSTERONE_STEP_STATUS.IN_PROGRESS && (
          <Badge size="x-small" color="yellow" className="hidden sm:flex">
            {t`Due now`}
          </Badge>
        )}
      </div>

      {actions && (
        <div className="row-start-2 flex flex-wrap items-center gap-1 sm:row-auto">
          {actions.map((action) => {
            return (
              <Fragment key={action.type}>
                <Button
                  onClick={() => handleAction(action)}
                  variant="tertiary"
                  className="gap-1 border-0 p-1 text-[#4B5647] underline"
                >
                  {ACTION_LABEL[action.type]}

                  {action.isExternal && (
                    <SquareBottomUpIcon className="size-3" />
                  )}
                </Button>

                {action.isExternal && (
                  <ExternalActionConfirmModal
                    open={showActionModal}
                    onOpenChange={closeActionModal}
                    action={action}
                    stepType={type}
                  />
                )}
              </Fragment>
            );
          })}
        </div>
      )}

      <CircleIcon
        size="xs"
        icon={
          status === TESTOSTERONE_STEP_STATUS.COMPLETED
            ? CheckCircleIcon
            : TESTOSTERONE_STEP_ICONS[type]
        }
        color={COLORS[status]}
      />
    </Card.Root>
  );
};
